import { useMemo, useRef, useState } from 'react';

import { ReactComponent as IconClose } from '../../../assets/images/blueTheme/X.svg';
import useOnClickOutside from '../../../hooks/useOnClickOutside/useOnClickOutside';
import { Option } from '../../types';
import DropdownContainer from './DropdownContainer/DropdownContainer';
import {
  CloseIconWrapper,
  ErrorBlock,
  IconWrapper,
  StyledChevronDown,
  StyledInput,
  StyledInputIcons,
  StyledInputText,
  StyledMultiSelectWrapper,
} from './styled';
import { getSelectionsText, isElementInArray } from './utils';

type Props = {
  loading: boolean;
  allItems: Option[];
  initiallySelected?: string[];
  maxSelectedItemsCount: number;
  searchValue?: string;
  total: number;
  onChange: (items: Option[]) => void;
  handleSearch?: (value: string) => void;
  onNextPage?: () => void;
  showSelectionAndSearchBlock?: boolean;
  errorMessage?: string;
  maxIsReachedText?: string;
  testId?: string;
};

const MultipleSelect = ({
  loading,
  allItems,
  initiallySelected = [],
  searchValue,
  total,
  maxSelectedItemsCount,
  onChange,
  handleSearch,
  onNextPage,
  showSelectionAndSearchBlock,
  errorMessage,
  maxIsReachedText,
  testId = 'multiselectInput',
}: Props): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<Option[]>(() => {
    return initiallySelected?.map((selectedItem) => {
      const option = allItems.find((item) => item.value === selectedItem);

      return (
        option || {
          label: selectedItem,
          value: selectedItem,
        }
      );
    });
  });

  const getItemsIds = (items: Option[]): string[] => {
    return items.map(({ value }) => value);
  };

  const isSelectAllNotAllowed = useMemo((): boolean => {
    const allItemIds = getItemsIds(allItems);
    const selectedItemsFromTheListCount = selectedItems.filter((option) => allItemIds.includes(option.value)).length;
    const countSelectedNotVisibleItems = selectedItems.length - selectedItemsFromTheListCount;

    return !!(!loading && total && total > maxSelectedItemsCount - countSelectedNotVisibleItems);
  }, [allItems, loading, maxSelectedItemsCount, selectedItems, total]);

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setIsOpen(false));

  const handleOpenDropdown = (): void => {
    setIsOpen((state) => !state);
  };

  const handleUnselectAll = (): void => {
    const allItemIds = getItemsIds(allItems);
    const newSelectedItems = selectedItems.filter((option) => !allItemIds.includes(option.value));
    setSelectedItems(newSelectedItems);
    onChange(newSelectedItems);
  };

  const handleSelectAll = (): void => {
    const selectedItemsIds = getItemsIds(selectedItems);
    const newSelectedItems = [...selectedItems];
    allItems.forEach((item) => {
      if (!selectedItemsIds.includes(item.value)) {
        newSelectedItems.push(item);
      }
    });

    setSelectedItems(newSelectedItems);
    onChange(newSelectedItems);
  };

  const clearSelections = (): void => {
    setSelectedItems([]);
    onChange([]);
  };

  const handleSelectItem = (item: Option): void => {
    setSelectedItems((state) => {
      const newState = isElementInArray(item, state)
        ? state.filter(({ value }) => value !== item.value)
        : [...state, item];
      onChange(newState);

      return newState;
    });
  };

  const handleUnselectItem = (item: Option): void => {
    setSelectedItems((state) => state.filter(({ value }) => value !== item.value));
    onChange(selectedItems.filter((el) => el !== item));
  };

  const showSelectionsText = useMemo(() => {
    if (loading || (allItems.length === 0 && selectedItems.length !== 0)) {
      return getSelectionsText(selectedItems, true);
    }

    if (selectedItems.length === 1) {
      const firstSelectedItem = allItems.find((item) => item.value === selectedItems[0]?.value);
      if (firstSelectedItem) {
        return getSelectionsText([firstSelectedItem], false);
      }
    }

    return getSelectionsText(selectedItems, false);
  }, [selectedItems, loading, allItems]);

  const tooltipString = selectedItems.map(({ label }) => label).join(`\n`);

  return (
    <StyledMultiSelectWrapper ref={ref} data-testid="multiselectWrapper">
      <StyledInput
        title={tooltipString}
        data-testid={testId}
        onClick={handleOpenDropdown}
        $error={!!errorMessage}
        $open={isOpen}
      >
        <StyledInputText>{showSelectionsText}</StyledInputText>
        <StyledInputIcons>
          {Boolean(selectedItems.length) && (
            <CloseIconWrapper
              data-testid="removeAll"
              onClick={(e): void => {
                clearSelections();
                e.stopPropagation();
              }}
            >
              <IconClose />
            </CloseIconWrapper>
          )}
          <IconWrapper>
            <StyledChevronDown data-testid="chevron" />
          </IconWrapper>
        </StyledInputIcons>
      </StyledInput>
      {isOpen && (
        <DropdownContainer
          loading={loading}
          selectedItems={selectedItems}
          allItems={allItems}
          total={total}
          maxSelectedItemsCount={maxSelectedItemsCount}
          isAllowedToSelectAll={!isSelectAllNotAllowed}
          handleSelectAll={handleSelectAll}
          handleUnselectAll={handleUnselectAll}
          handleSelectItem={handleSelectItem}
          handleUnselectItem={handleUnselectItem}
          handleSearch={handleSearch}
          searchValue={searchValue}
          onNextPage={onNextPage}
          showSelectionAndSearchBlock={showSelectionAndSearchBlock}
          maxIsReachedText={maxIsReachedText}
        />
      )}
      {errorMessage && <ErrorBlock data-testid="errorMessage">{errorMessage}</ErrorBlock>}
    </StyledMultiSelectWrapper>
  );
};

export default MultipleSelect;
